// Typed fetch helpers for the Pages Functions API. All reads are public.
// Writes pass through the same path; the server enforces Cf-Access OR
// service-token. From the UI we expect Cf-Access cookies set by the
// browser session — no headers needed here.

export interface BuildRow {
  id: string;
  repo: string;
  branch: string;
  sha: string;
  pr_number: number | null;
  triggered_at: number;
  status: string;
  story_count: number;
  changed_count: number;
  added_count: number;
  removed_count: number;
}

export interface DiffRow {
  id: string;
  story_id: string;
  story_title: string | null;
  viewport: string;
  theme: string;
  baseline_png_sha256: string | null;
  current_png_sha256: string | null;
  diff_png_sha256: string | null;
  state: "unchanged" | "changed" | "added" | "removed";
  pixel_delta: number;
  pixel_delta_pct: number;
  review_state: "pending" | "accepted" | "rejected";
  reviewer: string | null;
  baseline_url: string | null;
  current_url: string | null;
  diff_url: string | null;
}

export interface BuildDetail {
  build: BuildRow & {
    bundle_hash: string | null;
    unchanged_count: number;
  };
  diffs: DiffRow[];
}

async function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`${res.status} ${path}: ${body}`);
  }
  return res.json() as Promise<T>;
}

export async function listBuilds(query: { repo?: string; branch?: string; limit?: number } = {}) {
  const sp = new URLSearchParams();
  if (query.repo) sp.set("repo", query.repo);
  if (query.branch) sp.set("branch", query.branch);
  if (query.limit) sp.set("limit", String(query.limit));
  const qs = sp.toString();
  return fetchJSON<{ builds: BuildRow[] }>("/api/builds" + (qs ? "?" + qs : ""));
}

export async function getBuild(id: string) {
  return fetchJSON<BuildDetail>(`/api/builds/${id}`);
}

export async function acceptBuild(id: string, story?: string) {
  return fetchJSON<{ accepted: number }>(`/api/builds/${id}/accept`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ story: story ?? "" }),
  });
}

export async function promoteBuild(id: string, branch: string) {
  return fetchJSON<{ promoted: number }>(`/api/builds/${id}/promote`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ branch }),
  });
}
